import { Component, OnInit, OnDestroy, signal, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppService } from '@app/app.service';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';
import { ShowEmailDialogComponent } from '@app/shared/show-email-dialog/show-email-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';

@Component({
  selector: 'app-load-view-public',
  imports: [
    CommonModule,
    CurrencyPipe,
    DatePipe,
    MatCardModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    ClipboardModule,
    MatChipsModule,
    MatExpansionModule,
],
  templateUrl: './load-view-public.component.html',
  styleUrl: './load-view-public.component.scss',
})
export class LoadViewPublicComponent implements OnInit, OnDestroy {
  loadId!: number;
  load = signal<any>(null);
  loading = signal(false);

  isMobile = window.innerWidth < 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  constructor(
    private route: ActivatedRoute,
    private appService: AppService,
    private clipboard: Clipboard,
    private dialog: MatDialog
  ) {
    this.loadId = Number(this.route.snapshot.paramMap.get('id'));
  }

  ngOnInit(): void {
    this.getLoad();

    document.body.classList.add('public-load-page');
  }

  ngOnDestroy(): void {
    document.body.classList.remove('public-load-page');
  }

  getLoad(): void {
    this.loading.set(true);

    this.appService.get(`loads/view/${this.loadId}`).subscribe({
      next: (response: any) => {
        this.load.set(response.data);
        this.loading.set(false);
      },
      error: (error) => {
        console.error(error);
        this.load.set(null);
        this.loading.set(false);
      },
    });
  }

  formatPhone(phone: string | null | undefined): string {
    if (!phone) return '';

    const digits = phone.replace(/\D/g, '');

    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return phone;
  }

  getDimensions(load: any): string {
    const length = this.toFeetAndInches(load.length);
    const width = this.toFeetAndInches(load.width);
    const height = this.toFeetAndInches(load.height);

    const dimensions = [length, width, height]
      .filter((dim) => dim)
      .join(' × ');

    return dimensions;
  }

  toFeetAndInches(totalInches: number): string {
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet}-${inches}`;
  }

  getCarrierType(carrier: any): string {
    const isCarrier = carrier?.is_carrier ?? false;
    const isBroker = carrier?.is_broker ?? false;

    if (isBroker && isCarrier) return 'BROKER & CARRIER';
    if (isBroker) return 'BROKER';
    if (isCarrier) return 'CARRIER';

    return '';
  }

  toggleSaved(): void {
    const load = this.load();

    if (!load) {
      return;
    }

    const originalValue = load.is_saved;

    this.load.update(current => ({
      ...current,
      is_saved: !current.is_saved,
    }));

    this.appService
      .patch(`saved/loads/${load.id}/toggle-saved`, {})
      .subscribe({
        next: (res) => {
          this.load.update(current => ({
            ...current,
            is_saved: res.data.is_saved,
          }));
        },
        error: (err) => {
          this.load.update(current => ({
            ...current,
            is_saved: originalValue,
          }));

          this.appService.handleError(err);
        },
      });
  }

  copyLink(): void {
    const url = `${window.location.origin}/loads/view/${this.loadId}`;
    this.clipboard.copy(url);
    this.appService.showSnackBar('Link copied to clipboard.', 'success');
  }

  openGoogleMaps(): void {
    const origin = encodeURIComponent(this.load().origin);
    const destination = encodeURIComponent(this.load().destination);
    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;
    window.open(url, '_blank');
  }

  openMotus(): void {
    const USDOTNumber = this.load().carrier?.dot_number;
    const url = `https://motus.dot.gov/customer/${USDOTNumber}/account`;
    window.open(url, '_blank');
  }

  showUserEmail(): void {
    this.dialog.open(ShowEmailDialogComponent, {
      width: '400px',
      data: {
        url: `loads/view/${this.loadId}/email`,
        load: this.load(),
      },
    });
  }

  get tags(): string[] {
    const load = this.load();

    if (!load) {
      return [];
    }

    const tags: string[] = [];

    if (!load.operable) {
      tags.push('Non-Operable');
    }

    const tagMap: Record<string, string> = {
      tarp: 'Tarp',
      hazmat: 'Hazmat',
      military_base_access: 'Military Base Access',
      twic: 'TWIC',
      emodal: 'eModal',
      outriggers: 'Outriggers',
      divisible: 'Divisible',
      beam_trailer: 'Beam Trailer',
      extendable_trailer: 'Extendable Trailer',
      permits_reimbursed: 'Permits Reimbursed',
    };

    Object.entries(tagMap).forEach(([key, label]) => {
      if (load[key]) {
        tags.push(label);
      }
    });

    return tags;
  }
}